import { inject, Injectable } from '@angular/core';

import { PropertyInsurance } from 'src/shared/services/property-insurance.service';
import { SteadilyMockService } from 'src/shared/services/steadily-mock.service';

import { AddressModel } from 'src/models/address.model';
import {
  CoverageGapLine,
  InsuranceInsight,
  InsuranceInsightSeverity,
  InsurancePortfolioSummary,
  PolicyStatus,
} from 'src/models/insurance-insight.model';
import { SteadilyEstimate } from 'src/models/steadily.model';

/** Prefetched real Steadily estimates keyed by address id (see SteadilyService). */
export type EstimatesByAddress = Map<number, SteadilyEstimate>;

/** A policy inside this window is treated as needing a renewal decision. */
export const EXPIRING_SOON_DAYS = 45;

/** Coverage types that actually protect a rented property. */
const RENTAL_COVERAGE_TYPES = ['short_term_rental', 'landlord_rental_property'];

const SEVERITY_ORDER: Record<InsuranceInsightSeverity, number> = {
  critical: 0,
  warning: 1,
  opportunity: 2,
};

/**
 * Derives the insurance insight feed from the customer's own policies and properties.
 *
 * All of the reasoning here (expiration windows, coverage gaps, uninsured properties,
 * premium comparison) runs on real data. The only stubbed input is Steadily pricing,
 * which comes from SteadilyMockService until a partner API key is available.
 */
@Injectable({
  providedIn: 'root',
})
export class InsuranceInsightsService {

  private steadilyMock = inject(SteadilyMockService);

  /**
   * Returns the real Steadily estimate for an address when it was prefetched, otherwise
   * falls back to mock pricing so a missing or failed fetch never blanks an insight.
   */
  private resolveEstimate(address: AddressModel, estimates?: EstimatesByAddress): SteadilyEstimate {
    return estimates?.get(address.id) || this.steadilyMock.buildEstimateForAddress(address);
  }

  /** Parses a 'YYYY-MM-DD' date at local noon so timezone never shifts the day. */
  private parseDate(value: string): Date | null {
    if (!value) { return null; }
    const parts = value.slice(0, 10).split('-').map(Number);
    if (parts.length !== 3 || parts.some(isNaN)) { return null; }
    return new Date(parts[0], parts[1] - 1, parts[2], 12, 0, 0);
  }

  daysUntil(dateValue: string, today = new Date()): number | null {
    const target = this.parseDate(dateValue);
    if (!target) { return null; }
    const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 12, 0, 0);
    return Math.round((target.getTime() - startOfToday.getTime()) / 86400000);
  }

  buildPolicyStatus(insurance: PropertyInsurance, today = new Date()): PolicyStatus {
    const days = this.daysUntil(insurance.expiration_date, today);
    if (days === null) {
      return {
        kind: 'unverified',
        label: 'No Expiration',
        detail: 'Add an expiration date to track renewals',
      };
    }
    if (days < 0) {
      return {
        kind: 'lapsed',
        label: 'Expired',
        detail: `Expired ${Math.abs(days)} ${Math.abs(days) === 1 ? 'day' : 'days'} ago`,
      };
    }
    if (days <= EXPIRING_SOON_DAYS) {
      return {
        kind: 'expiring_soon',
        label: `Expires in ${days}d`,
        detail: insurance.auto_renew ? 'Auto-renew is on' : 'Auto-renew is off',
      };
    }
    return {
      kind: 'active',
      label: 'Active',
      detail: insurance.auto_renew ? 'Auto-renew is on' : 'Auto-renew is off',
    };
  }

  buildPolicyStatuses(insurances: PropertyInsurance[], today = new Date()): Record<number, PolicyStatus> {
    const statuses: Record<number, PolicyStatus> = {};
    insurances.forEach(insurance => {
      statuses[insurance.id] = this.buildPolicyStatus(insurance, today);
    });
    return statuses;
  }

  buildInsights(
    insurances: PropertyInsurance[],
    addresses: AddressModel[],
    today = new Date(),
    estimates?: EstimatesByAddress
  ): InsuranceInsight[] {
    const insights: InsuranceInsight[] = [];
    const policiesByAddress = new Map<number, PropertyInsurance[]>();
    insurances.forEach(insurance => {
      const addressId = insurance.address?.id;
      if (!addressId) { return; }
      const existing = policiesByAddress.get(addressId) || [];
      existing.push(insurance);
      policiesByAddress.set(addressId, existing);
    });

    addresses.forEach(address => {
      const policies = policiesByAddress.get(address.id) || [];
      const addressName = address.address_name || address.address1 || 'Unknown Property';
      const addressColor = address.color || '#8790A0';

      if (policies.length === 0) {
        insights.push(this.buildUninsuredInsight(address, addressName, addressColor, estimates));
        return;
      }

      policies.forEach(policy => {
        const expiringInsight = this.buildExpirationInsight(policy, address, addressName, addressColor, today, estimates);
        if (expiringInsight) { insights.push(expiringInsight); }

        const gapInsight = this.buildCoverageGapInsight(policy, address, addressName, addressColor, estimates);
        if (gapInsight) { insights.push(gapInsight); }

        const savingsInsight = this.buildSavingsInsight(policy, address, addressName, addressColor, estimates);
        if (savingsInsight) { insights.push(savingsInsight); }
      });

      const mismatchInsight = this.buildCoverageMismatchInsight(policies, address, addressName, addressColor, estimates);
      if (mismatchInsight) { insights.push(mismatchInsight); }
    });

    return insights.sort((a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity]);
  }

  private buildUninsuredInsight(
    address: AddressModel,
    addressName: string,
    addressColor: string,
    estimates?: EstimatesByAddress
  ): InsuranceInsight {
    const estimate = this.resolveEstimate(address, estimates);
    const annual = estimate.estimate.lowest;
    return {
      id: `uninsured-${address.id}`,
      kind: 'uninsured',
      severity: 'critical',
      propertyName: addressName,
      propertyColor: addressColor,
      addressId: address.id,
      icon: 'assets/svg/shield-outline.svg',
      title: 'No policy on file',
      detail: 'This property has no coverage tracked in TIDY. A total loss would be uninsured.',
      primaryCta: 'See Estimate',
      secondaryCta: 'Add Existing Policy',
      secondaryAction: 'add_policy',
      estimate,
      monthlyPremium: Math.round(annual / 12),
      annualPremium: annual,
    };
  }

  private buildExpirationInsight(
    policy: PropertyInsurance,
    address: AddressModel,
    addressName: string,
    addressColor: string,
    today: Date,
    estimates?: EstimatesByAddress
  ): InsuranceInsight | null {
    const days = this.daysUntil(policy.expiration_date, today);
    if (days === null) {
      return {
        id: `no-expiration-${policy.id}`,
        kind: 'expiring',
        severity: 'warning',
        propertyName: addressName,
        propertyColor: addressColor,
        policyId: policy.id,
        icon: 'assets/svg/time-outline.svg',
        title: 'No expiration date on file',
        detail: `${policy.provider_name || 'This policy'} has no expiration date, so TIDY can't warn you before it lapses.`,
        primaryCta: 'Add Expiration Date',
        primaryAction: 'edit_policy',
      };
    }
    if (days < 0) {
      return {
        id: `expired-${policy.id}`,
        kind: 'expiring',
        severity: 'critical',
        propertyName: addressName,
        propertyColor: addressColor,
        policyId: policy.id,
        icon: 'assets/svg/alert-circle-outline.svg',
        title: `Policy expired ${Math.abs(days)} ${Math.abs(days) === 1 ? 'day' : 'days'} ago`,
        detail: `${policy.provider_name || 'This policy'} expired and may no longer be in force. Confirm with your carrier or replace the coverage.`,
        primaryCta: 'Shop Replacement',
        secondaryCta: 'Update Policy',
        secondaryAction: 'edit_policy',
        estimate: this.resolveEstimate(address, estimates),
      };
    }
    if (days <= EXPIRING_SOON_DAYS && !policy.auto_renew) {
      return {
        id: `expiring-${policy.id}`,
        kind: 'expiring',
        severity: 'warning',
        propertyName: addressName,
        propertyColor: addressColor,
        policyId: policy.id,
        icon: 'assets/svg/time-outline.svg',
        title: `Expires in ${days} ${days === 1 ? 'day' : 'days'}`,
        detail: `${policy.provider_name || 'This policy'} expires soon and auto-renew is off. Shop rates now to avoid a lapse.`,
        primaryCta: 'Shop Rates',
        secondaryCta: 'Update Policy',
        secondaryAction: 'edit_policy',
        estimate: this.resolveEstimate(address, estimates),
      };
    }
    return null;
  }

  private buildCoverageGapInsight(
    policy: PropertyInsurance,
    address: AddressModel,
    addressName: string,
    addressColor: string,
    estimates?: EstimatesByAddress
  ): InsuranceInsight | null {
    if (!policy.coverage_amount) { return null; }
    // Coverage gaps are measured only against a real Steadily estimate.
    const estimate = estimates?.get(address.id);
    if (!estimate) { return null; }
    const replacementValue = estimate.calculated_property.property_details.replacement_value;
    const coverageDollars = policy.coverage_amount / 100;
    if (coverageDollars >= replacementValue * 0.9) { return null; }

    const shortfall = Math.round(replacementValue - coverageDollars);
    const recommended = estimate.product_options[0]?.coverages;
    if (!recommended) { return null; }

    // Coverage fields are individually optional on real estimates; format what's present
    // and fall back to a placeholder rather than calling toLocaleString on undefined.
    const dollars = (amount?: number): string =>
      amount === null || amount === undefined ? 'See quote' : `$${amount.toLocaleString('en-US')}`;

    const gapLines: CoverageGapLine[] = [
      {
        label: 'Dwelling limit',
        yours: `$${coverageDollars.toLocaleString('en-US')}`,
        recommended: dollars(recommended.dwelling_limit),
        isGap: true,
      },
      {
        label: 'Other structures',
        yours: 'Not tracked',
        recommended: dollars(recommended.private_structures_limit),
        isGap: true,
      },
      {
        label: 'Loss of rents',
        yours: 'Not tracked',
        recommended: dollars(recommended.loss_of_rents_limit),
        isGap: true,
      },
      {
        label: 'Deductible',
        yours: policy.deductible_amount ? `$${(policy.deductible_amount / 100).toLocaleString('en-US')}` : 'Not tracked',
        recommended: recommended.all_other_perils_deductible || 'See quote',
        isGap: !policy.deductible_amount,
      },
      {
        label: 'Loss valuation',
        yours: 'Not tracked',
        recommended: recommended.loss_valuation || 'See quote',
        isGap: true,
      },
    ];

    return {
      id: `underinsured-${policy.id}`,
      kind: 'underinsured',
      severity: 'warning',
      propertyName: addressName,
      propertyColor: addressColor,
      policyId: policy.id,
      icon: 'assets/svg/alert-circle-outline.svg',
      title: `Possibly underinsured by $${shortfall.toLocaleString('en-US')}`,
      detail: `Your coverage is below the estimated replacement cost of $${replacementValue.toLocaleString('en-US')} for this property.`,
      primaryCta: 'Compare Coverage',
      secondaryCta: 'Edit Policy',
      secondaryAction: 'edit_policy',
      estimate,
      gapLines,
    };
  }

  private buildSavingsInsight(
    policy: PropertyInsurance,
    address: AddressModel,
    addressName: string,
    addressColor: string,
    estimates?: EstimatesByAddress
  ): InsuranceInsight | null {
    if (!policy.annual_premium_amount) { return null; }
    // A savings claim, and the header's savings total built from it, needs a real Steadily price.
    const estimate = estimates?.get(address.id);
    if (!estimate) { return null; }
    const currentAnnual = policy.annual_premium_amount / 100;
    const quoted = estimate.estimate.lowest;
    const savings = Math.round(currentAnnual - quoted);
    if (savings < currentAnnual * 0.1 || savings < 100) { return null; }

    return {
      id: `savings-${policy.id}`,
      kind: 'overpaying',
      severity: 'opportunity',
      propertyName: addressName,
      propertyColor: addressColor,
      policyId: policy.id,
      icon: 'assets/svg/trending-down-outline.svg',
      title: `You could save $${savings.toLocaleString('en-US')} a year`,
      detail: `You pay $${Math.round(currentAnnual).toLocaleString('en-US')}/yr with ${policy.provider_name || 'your current carrier'}. Steadily estimates $${quoted.toLocaleString('en-US')}/yr for comparable coverage.`,
      primaryCta: 'See Quote',
      secondaryCta: 'Dismiss',
      secondaryAction: 'dismiss',
      estimate,
      monthlyPremium: Math.round(quoted / 12),
      annualPremium: quoted,
      savingsAmount: savings,
    };
  }

  private buildCoverageMismatchInsight(
    policies: PropertyInsurance[],
    address: AddressModel,
    addressName: string,
    addressColor: string,
    estimates?: EstimatesByAddress
  ): InsuranceInsight | null {
    const hasRentalCoverage = policies.some(policy => RENTAL_COVERAGE_TYPES.includes(policy.coverage_type));
    if (hasRentalCoverage) { return null; }
    const provider = policies[0]?.provider_name || 'Your policy';

    return {
      id: `mismatch-${policies[0]?.id}`,
      kind: 'coverage_mismatch',
      severity: 'warning',
      propertyName: addressName,
      propertyColor: addressColor,
      policyId: policies[0]?.id,
      icon: 'assets/svg/alert-circle-outline.svg',
      title: 'No landlord or rental coverage',
      detail: `${provider} is not a landlord or short-term rental policy. Standard homeowners policies often deny claims on rented properties.`,
      primaryCta: 'See Landlord Options',
      secondaryCta: 'Edit Policy',
      secondaryAction: 'edit_policy',
      estimate: this.resolveEstimate(address, estimates),
    };
  }

  buildSummary(
    insurances: PropertyInsurance[],
    addresses: AddressModel[],
    insights: InsuranceInsight[]
  ): InsurancePortfolioSummary {
    const totalAnnualPremium = insurances.reduce(
      (sum, insurance) => sum + (insurance.annual_premium_amount || 0) / 100,
      0
    );
    const coveredAddressIds = new Set(
      insurances.map(insurance => insurance.address?.id).filter(Boolean)
    );
    const potentialAnnualSavings = insights.reduce(
      (sum, insight) => sum + (insight.savingsAmount || 0),
      0
    );

    return {
      totalAnnualPremium: Math.round(totalAnnualPremium),
      propertiesCovered: coveredAddressIds.size,
      propertiesTotal: addresses.length,
      needsAttentionCount: insights.length,
      potentialAnnualSavings: Math.round(potentialAnnualSavings),
    };
  }
}
